import { Button, Card, Field, Flex, Input, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import useCustomToast from '../../../hooks/useCustomToast'
import { SubmitHandler, useForm } from 'react-hook-form'
import { ApiError, FormsNewsletterService, FormsNewsletterUnsubscribe } from '../../../client'
import { handleError } from '../../../utils'
import { useReCaptcha } from '../../../hooks/useReCaptcha'
import { useSettings } from '../../../components/UI/settings-context'
import NotFound from '../../../components/Common/NotFound'
import { Helmet } from 'react-helmet-async'
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT

export const Route = createFileRoute('/_layout/newsletter/abmelden')({
    component: Abmelden,
    notFoundComponent: NotFound,
})

function Abmelden() {
    const policy = useSettings()?.policy

    const siteKey = policy?.captcha_id ?? ""
    const { getToken } = useReCaptcha(siteKey, "newsletter_unsubscribe_form")

    const showToast = useCustomToast()

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<FormsNewsletterUnsubscribe>({
        mode: "onBlur",
        criteriaMode: "all",
        defaultValues: {
            email: "",
        },
    })

    const mutation = useMutation({
        mutationFn: (data: { token: string; body: FormsNewsletterUnsubscribe }) =>
            FormsNewsletterService.formsNewsletterUnsubscribe({ recaptchaToken: data.token, requestBody: data.body }),
        onSuccess: () => {
            showToast("Abgemeldet!", "Newsletter-Abmeldung erhalten, Mail mit Bestätigungslink wurde gesendet", "success")
            reset()
        },
        onError: (err: ApiError) => {
            handleError(err, showToast)
        },
    })

    const onSubmit: SubmitHandler<FormsNewsletterUnsubscribe> = async (data) => {
        const isTestEnv = ["local", "CI"].includes(ENVIRONMENT)

        // reCAPTCHA
        let token: string | null = "test-token"

        if (!isTestEnv) {
            token = await getToken()
            if(!token) {
                showToast("Fehler!", "reCAPTCHA konnte nicht ausgeführt werden", "error")
                return
            }
        }

        mutation.mutate({ token, body: data })
    }

    return (
        <>
            <Helmet>
                {policy?.captcha_id && <script src={`https://www.google.com/recaptcha/api.js?render=${policy.captcha_id}`} /> }
            </Helmet>

            <Card.Root size="lg">
                <Card.Header>
                    <Card.Title textAlign="center" color="main">Newsletter abmelden</Card.Title>

                    <Card.Description textAlign="center">
                        Schade... wollen Sie sich wirklich vom wöchentlichen Newsletter der Wochen-Zeitung abmelden?
                    </Card.Description>
                </Card.Header>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Card.Body alignItems="center">
                        <Field.Root required invalid={!!errors.email} mt={6} width="30em">
                            <Field.Label htmlFor="email">
                                E-Mail
                                <Field.RequiredIndicator />
                            </Field.Label>

                            <Input
                                id="email"
                                {...register("email", {
                                    required: "E-Mail ist erforderlich",
                                    maxLength: {
                                        value: 255,
                                        message: "E-Mail darf maximal 255 Zeichen lang sein",
                                    },
                                })}
                                placeholder="E-Mail"
                                type="email"
                            />
                            {errors.email && (
                                <Field.ErrorText>{errors.email.message}</Field.ErrorText>
                            )}
                        </Field.Root>

                        <Flex justifyContent="center" mt={6}>
                            <Text color="dim" fontSize="sm">Diese Website ist durch reCAPTCHA geschützt.</Text>
                        </Flex>
                    </Card.Body>

                    <Card.Footer justifyContent="center">
                        <Button
                            mt={6}
                            variant="primary"
                            textTransform="uppercase"
                            type="submit"
                            loading={isSubmitting}
                        >
                            Abmelden
                        </Button>
                    </Card.Footer>
                </form>
            </Card.Root>
        </>
    )
}
